import './App.css'
import ReactGA from 'react-ga4';
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ClientProvider } from './contexts/ClientContext';
import { AgentClientChatProvider } from './contexts/AgentClientChatContext';
import { GlobalChatProvider } from './contexts/GlobalChatContext';
import ProtectedRoute from './components/ProtectedRoute';
import HomeAttachments from './pages/HomeAttachments';
import HomeAreas from './pages/HomeAreas';
import PricingPage from './pages/Pricing';
import { useEffect } from 'react';
import { HelmetProvider } from "react-helmet-async";
import AdminGuard from './components/AdminGuard';
import GlobalChat from './components/GlobalChat';

import Home from "./pages/Home";
import NoPage from './pages/NoPage';
import Homes from './pages/Homes';
import LoginPage from './components/Login';
import HomeDetails from './pages/HomeDetails';
import HomeChat from './pages/HomeChat';
import Profile from './pages/Profile';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import RefundPolicy from './pages/RefundPolicy';
import Disclaimer from './pages/Disclaimer';
import Contact from './pages/Contact';
import ClientProfile from './pages/ClientProfile';
import AgentChatPage from './pages/AgentChatPage';
import ComingSoon from './pages/ComingSoon';
import HomeAnalytics from './pages/HomeAnalytics';
import PropertyIntelligenceWrapper from './components/PropertyIntelligenceWrapper';
import AppDownload from './pages/AppDownload';
import { BusinessProvider } from './contexts/BusinessContext';
import { BusinessAdminProvider } from './contexts/BusinessAdminContext';
import { BusinessAgentProvider } from './contexts/BusinessAgentContext';
import ProtectedBusinessRoute from './components/ProtectedBusinessRoute';
import BusinessAdminPage from './components/business/AdminPage';
import AgentDashboard from './components/business/AgentDashboard';
import ReferralsPage from './pages/ReferralsPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminBusinesses from './pages/admin/AdminBusinesses';
import AdminContent from './pages/admin/AdminContent';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminSystem from './pages/admin/AdminSystem';
import AdminBilling from './pages/admin/AdminBilling';
import AdminTools from './pages/admin/AdminTools';

const TRACKING_IDS = {
  production: 'G-BJM1SG0TLQ',
  development: 'G-167G1X4046'
};

ReactGA.initialize(window.location.hostname === 'cribcritic.ai' ? TRACKING_IDS.production : TRACKING_IDS.development, {
  gaOptions: {
    // This helps identify which domain the traffic is coming from
    cookieDomain: 'auto'
  }
});

/**
 * AuthenticatedGlobalChatWrapper - Wraps the entire app in GlobalChatProvider when authenticated
 * This allows any authenticated component (like Profile) to access the global chat context
 * to control the chat FAB visibility.
 */
function AuthenticatedGlobalChatWrapper({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  // Only wrap in GlobalChatProvider for authenticated users
  if (!user) {
    return <>{children}</>;
  }

  return (
    <GlobalChatProvider>
      {children}
      <GlobalChat />
    </GlobalChatProvider>
  );
}

function App() {
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: window.location.pathname });
  }, []);
  
  return (
    <HelmetProvider>
      <AuthProvider>
        <ClientProvider>
          <AgentClientChatProvider>
            <AuthenticatedGlobalChatWrapper>
              <div className="App">
                <Routes>
                {/* Public routes without client context */}
                <Route path="/" element={<Home />} />
                <Route path="/app" element={<AppDownload />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/refunds" element={<RefundPolicy />} />
                <Route path="/disclaimer" element={<Disclaimer />} />
                
                {/* Public Agent Profile Pages */}
                <Route path="/agent/:agentEmail" element={<ComingSoon />} />
                
                {/* Protected client routes with client context */}
                <Route path="/*" element={
                  <Routes>
                    <Route path="/homes" element={<ProtectedRoute><Homes /></ProtectedRoute>} />
                    <Route path="/agent-chat" element={<ProtectedRoute><AgentChatPage /></ProtectedRoute>} />
                    <Route path="/home/:homeId" element={<ProtectedRoute><HomeDetails /></ProtectedRoute>}>
                      <Route path="chat" element={<HomeChat />} />
                      <Route path="attachments" element={<HomeAttachments />} />
                      <Route path="areas" element={<HomeAreas />} />
                    </Route>
                    <Route path="/home/:homeId/intelligence" element={
                      <ProtectedRoute>
                        <PropertyIntelligenceWrapper />
                      </ProtectedRoute>
                    } />
                    <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

                    {/* System Admin routes */}
                    <Route path="/admin/*" element={
                      <ProtectedRoute>
                        <AdminGuard>
                          <Routes>
                            <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
                            <Route path="/dashboard" element={<AdminDashboard />} />
                            <Route path="/users" element={<AdminUsers />} />
                            <Route path="/businesses" element={<AdminBusinesses />} />
                            <Route path="/content" element={<AdminContent />} />
                            <Route path="/analytics" element={<AdminAnalytics />} />
                            <Route path="/system" element={<AdminSystem />} />
                            <Route path="/billing" element={<AdminBilling />} />
                            <Route path="/tools" element={<AdminTools />} />
                            <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
                          </Routes>
                        </AdminGuard>
                      </ProtectedRoute>
                    } />

                    {/* Business routes with nested protection */}
                    <Route path="/business/*" element={
                      <ProtectedRoute>
                        <BusinessProvider>
                          <Routes>
                            {/* Admin routes need business admin context */}
                            <Route path="/admin/*" element={
                              <ProtectedBusinessRoute>
                                <BusinessAdminProvider>
                                  <Routes>
                                    <Route path="/" element={<BusinessAdminPage />} />
                                    <Route path="/referrals" element={<ReferralsPage />} />
                                    {/* Future admin subroutes would go here */}
                                  </Routes>
                                </BusinessAdminProvider>
                              </ProtectedBusinessRoute>
                            } />
                            
                            {/* Agent routes need business agent context */}
                            <Route path="/agent/*" element={
                              <ProtectedBusinessRoute>
                                <BusinessAgentProvider>
                                  <Routes>
                                    <Route path="/" element={<AgentDashboard />} />
                                    <Route path="/client/:clientEmail" element={<ClientProfile />} />
                                    <Route path="/client/:clientEmail/analytics/:homeId" element={<HomeAnalytics />} />
                                    <Route path="/referrals" element={<ReferralsPage />} />
                                    {/* Future agent subroutes would go here */}
                                  </Routes>
                                </BusinessAgentProvider>
                              </ProtectedBusinessRoute>
                            } />
                            
                            {/* Redirect /business to appropriate dashboard based on user role */}
                            <Route path="/" element={<Navigate to="/business/agent" replace />} />
                            
                            {/* Any other non-admin business routes would go here */}
                          </Routes>
                        </BusinessProvider>
                      </ProtectedRoute>
                    } />
                    
                    <Route path="*" element={<NoPage />} />
                  </Routes>
                } />
              </Routes>
              </div>
            </AuthenticatedGlobalChatWrapper>
          </AgentClientChatProvider>
        </ClientProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
